/**
 * Sign in.
 *
 * Two decisions worth naming, both about what the page refuses to do.
 *
 * **It does not say which half was wrong.** The API answers a wrong address and
 * a wrong password identically, and the screen keeps that: telling somebody
 * "no such account" turns this form into a way of asking whether a particular
 * person volunteers for Parinaam.
 *
 * **It does not offer a way to register.** Accounts are made by an admin. A
 * volunteer who has not yet been onboarded wants the public registration form,
 * which is a different thing and is linked as such.
 */

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { BrandLogo, Button, PasswordInput, TextInput } from '../../components/common';
import { useAuth } from '../../hooks/useAuth';
import { useImpact } from '../../hooks/useImpact';
import { EMAIL_MESSAGE, isEmailish } from '@parinaam-vms/shared';
import { ApiRequestError } from '../../services/api/client';
import styles from './SignInPage.module.css';

/**
 * The four states a volunteer's hour passes through, in order.
 *
 * Not marketing steps — these are the system's own transitions, and each is
 * reachable only from the one before it: a registration request approved into a
 * volunteer, that volunteer assigned to an activity, attendance recorded
 * against a session of it, and that attendance approved, which is the moment the
 * hour counts anywhere. The figure for hours below counts only rows that
 * reached the last one.
 */
const LIFECYCLE = ['Registered', 'Assigned', 'Hours logged', 'Signed off'];

export function SignInPage() {
  const { user, loading, signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  /*
   * Always the landing page, never where they were before.
   *
   * This used to read `location.state.from`, set by whichever guard sent them
   * here, and return them to it. The effect on a shared machine was that
   * signing out and signing back in — as anyone, including the same person —
   * reopened the last session’s page.
   *
   * `/` is the index for both audiences: the Dashboard for staff, their own
   * volunteering for a volunteer.
   */
  if (!loading && user) return <Navigate to="/" replace />;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Enter your email address and password.');
      return;
    }

    /*
     * The shape, before the round trip (9 Oct 2026).
     *
     * The sign-in form was the one place that asked for an address and checked
     * nothing. A typo came back as "that email address and password do not
     * match" — which is true, and sends somebody looking at their password.
     *
     * No account enumeration concern here: this says the text is not an
     * address, not that no such account exists.
     */
    if (!isEmailish(email)) {
      setError(EMAIL_MESSAGE);
      return;
    }

    setPending(true);
    try {
      await signIn(email.trim(), password);
      // No navigation here: signing in sets the user, and the guard above
      // redirects on the next render. Doing both would race.
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Could not reach the system. Please try again.'
      );
      setPassword('');
    } finally {
      setPending(false);
    }
  };

  return (
    <main className={styles.page}>
      {/*
        The words beside the card, and they are decorative in the strict sense:
        everything that matters is in the form. They are dropped below 900px
        rather than stacked, because stacked they would push the password field
        under the fold on the phone half the coordinators use — and the page is
        still on the brand canvas there, so nothing about it looks unfinished.
      */}
      {/*
        The mark, enlarged and sitting behind the words.

        `logo-mark.svg` rather than `logo.svg`: at this size the wordmark beside
        the emblem is either illegible or enormous, and the words of the panel
        are already saying the name. It is drawn in its own blue, teal and sun —
        no mask, no filter — which is what the light field exists for.
      */}
      <div className={styles.watermark} aria-hidden="true" />

      <aside className={styles.brandPanel} aria-hidden="true">
        {/*
          The headline is about the volunteer, not about the software — and it
          rhymes, which is the reason it is one line rather than two.

          "spend" rather than "give": you spend time, and the rhyme with "end"
          is the whole line. "honoured" is the word doing the work; counting is
          the system's half of the bargain and it is already said twice below,
          in the trace and in the figures.
        */}
        <p className={styles.brandEyebrow}>Parinaam Foundation · Volunteer Management</p>
        <p className={styles.brandLine}>Every hour they spend, honoured till the end.</p>
        <div className={styles.brandRule} />
        <p className={styles.brandBlurb}>
          Every registration, every hour, every approval — kept, not remembered.
        </p>
        <ol className={styles.arc}>
          {LIFECYCLE.map((stage) => (
            <li key={stage} className={styles.arcStep}>
              {stage}
            </li>
          ))}
        </ol>
        <ImpactFigures />
      </aside>

      <div className={styles.card}>
        {/*
          The mark leads, left-aligned above the heading rather than centred
          over it. Centred marks read as a consumer sign-up; a lockup aligned
          with the text beneath it is what an internal tool looks like — and
          sign-in is the one screen where somebody may be checking they are on
          the right site, so the mark is at a size they can recognize.
        */}
        <header className={styles.header}>
          <BrandLogo size="lockup" />
          <h1 className={styles.title}>Sign in</h1>
          <p className={styles.subtitle}>Staff and volunteer access to the portal.</p>
        </header>

        <form onSubmit={submit} noValidate className={styles.form}>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <TextInput
            label="Email address"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <PasswordInput
            label="Password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <Button type="submit" variant="primary" disabled={pending} className={styles.submit}>
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className={styles.footnote}>
          Accounts are created by Parinaam. If you would like to volunteer,{' '}
          <Link to="/register">register here</Link>.
        </p>
      </div>
    </main>
  );
}

/**
 * Three real figures, or none.
 *
 * ---------------------------------------------------------------------------
 * Why they are small, and why nothing here makes them bigger
 * ---------------------------------------------------------------------------
 * These come from the database — volunteers on the roll, hours approved,
 * programs running — and on a fresh installation they are single digits. The
 * temptation on a sign-in page is a rounded-up, aspirational number; a figure
 * here is a claim Parinaam is making to anyone who opens the URL, so it is the
 * true one or it is absent.
 *
 * What makes a small number acceptable is the framing, not the number. These
 * are set at label scale with the count beside them, rather than as hero
 * figures under a boast — so "3 volunteers on the roll" reads as a system that
 * has just been set up, which is what it is, and the same markup carries 30,000
 * without changing.
 *
 * Absent, not zero, while loading or on failure: a strip that flashes three
 * zeros and then corrects itself is worse than one that simply arrives.
 */
function ImpactFigures() {
  const { data } = useImpact();

  if (!data) return null;

  // Indian digit grouping — 1,00,000 rather than 100,000. The organisation is
  // in Bengaluru and so is almost everyone reading this.
  const count = (value: number) => new Intl.NumberFormat('en-IN').format(value);

  const figures = [
    { value: data.volunteers, label: data.volunteers === 1 ? 'volunteer' : 'volunteers' },
    { value: data.hours, label: data.hours === 1 ? 'hour given' : 'hours given' },
    { value: data.programs, label: data.programs === 1 ? 'program' : 'programs' },
  ];

  return (
    <dl className={styles.figures}>
      {figures.map((figure) => (
        <div key={figure.label} className={styles.figure}>
          <dt className={styles.figureValue}>{count(figure.value)}</dt>
          <dd className={styles.figureLabel}>{figure.label}</dd>
        </div>
      ))}
    </dl>
  );
}
